import React, { useState, useEffect } from "react";
import { API_BASE } from "../config"; 

// Utility: Date Formatting
function formatDateToDDMMYYYY(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date)) return "";
  const day = ("0" + date.getDate()).slice(-2);
  const month = ("0" + (date.getMonth() + 1)).slice(-2);
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
}

const PrescriptionPrint = ({ show, data, companyDetails, onClose }) => {
  const [showHeader, setShowHeader] = useState(true);
  
  // ✅ Pura settings object store karne ke liye state
  const [settings, setSettings] = useState(null);

  // ✅ Fetching Settings for Footer Texts
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const token = sessionStorage.getItem('token'); 
        const response = await fetch(`${API_BASE}/Settings`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
          }
        });
        
        if (response.ok) {
          const result = await response.json();
          // Pura result object save kar lenge
          setSettings(result);
        }
      } catch (error) {
        console.error("Error fetching receipt footer settings:", error);
      }
    };

    if (show) {
      fetchSettings();
    }
  }, [show]);

  if (!show || !data || !companyDetails) return null;

  const handlePrint = () => {
    window.print();
  };

  const handleToggleHeader = () => {
    setShowHeader(!showHeader);
  };

  // --- Data Mapping ---
  const patientName = data.patientName || data.name || "";
  const uhid = data.uhidNo || "-";
  const mobile = data.mobileNo || "";
  const ageGender = `${data.age || "-"}/${data.gender || "-"}`;
  const doctorName = data.doctorName || "Dr. .......................";
  
  const visitType = data.visitType || "OPD"; // "OPD" ya "IPD"
  const receiptNo = data.visitSrNo || data.visitId || "-";
  const prescriptionDate = data.prescriptionDate || new Date();

  const chiefComplaints = data.chiefComplaints || "";
  const diagnosis = data.diagnosis || "";
  const notes = data.notes || ""; 

  const medicines = data.medicines || [];

  // Vitals Extraction
  const vitals = {
    Weight: data.weights || data.weight || "",
    Height: data.heights || data.height || "",
    BP: data.bp || data.BP || "",
    Pulse: data.pulses || data.pulse || "",
    Temp: data.temps || data.temperature || "",
    SPO2: data.oxy || data.oxygen || "",
    RBS: data.rbs || data.RBS || ""
  };

  // 🌐 Base URL for dynamic images
  const getImageUrl = (path) => {
    if (!path) return "";
    const base = API_BASE.replace(/\/api\/?$/, ''); 
    const cleanPath = path.replace(/^\//, ''); 
    return `${base}/${cleanPath}`;
  };

  const mainLogoPath = companyDetails?.logo || companyDetails?.Logo;
  const ayushmanLogoPath = companyDetails?.ayushmanLogo || companyDetails?.AyushmanLogo;

  // 🔥 Pagination Logic
  const ITEMS_PER_PAGE = 8; 
  const pages = [];
  
  if (medicines.length === 0) {
    pages.push([]);
  } else {
    for (let i = 0; i < medicines.length; i += ITEMS_PER_PAGE) {
      pages.push(medicines.slice(i, i + ITEMS_PER_PAGE));
    }
  }

  // ✅ DYNAMIC FOOTER LOGIC
  let currentFooterText = "";
  if (settings) {
      if (visitType.toUpperCase() === "OPD") {
          currentFooterText = settings.opdReceiptFooterText || "";
      } else if (visitType.toUpperCase() === "IPD") {
          currentFooterText = settings.ipdReceiptFooterText || "";
      }
  }

  return (
    <>
      <style>{`
        :root {
          --marg-border: 1px solid #000;
          --marg-border-thick: 2px solid #000;
          --marg-font: Arial, Helvetica, sans-serif;
        }
        .marg-receipt-wrapper { font-family: var(--marg-font); color: #000; background: #fff; font-size: 12px; }
        
        .marg-outer-box { 
          border: var(--marg-border-thick); margin-bottom: 20px; height: 265mm; max-height: 265mm; 
          box-sizing: border-box; position: relative; background: #fff; display: flex; 
          flex-direction: column; page-break-inside: avoid; page-break-after: always; overflow: hidden;
        }
        .marg-outer-box:last-child { page-break-after: auto; }

        .watermark-bg {
          position: absolute; top: 55%; left: 50%; transform: translate(-50%, -50%);
          width: 350px; opacity: 0.08; pointer-events: none; z-index: 0;
          -webkit-print-color-adjust: exact; print-color-adjust: exact;
        }

        .marg-header, .marg-title-strip, .marg-patient-box, .marg-rx-container, .marg-signature-box {
          position: relative; z-index: 1;
        }

        .marg-header { 
          border-bottom: var(--marg-border-thick); padding: 5px 15px; text-align: center; 
          display: flex; justify-content: space-between; align-items: center; background: #fff;
        }
        
        .marg-title-strip { 
          background: #f0f0f0; border-bottom: var(--marg-border-thick); padding: 4px 15px; 
          font-weight: 900; font-size: 14px; letter-spacing: 1px; text-align: center; 
        }
        
        .marg-patient-box { display: flex; border-bottom: var(--marg-border-thick); }
        .marg-patient-col { padding: 5px 10px; }
        .marg-patient-left { width: 60%; border-right: 2px solid #000; }
        .marg-patient-right { width: 40%; }
        .marg-patient-col table { width: 100%; font-size: 12px; line-height: 1.5; }
        .marg-patient-col table td { vertical-align: top; }
        
        .marg-rx-container { flex-grow: 1; display: flex; border-bottom: var(--marg-border-thick); }
        
        .marg-rx-left { width: 25%; border-right: var(--marg-border-thick); padding: 10px; display: flex; flex-direction: column; gap: 15px; }
        .marg-rx-right { width: 75%; padding: 15px 20px; display: flex; flex-direction: column; }
        
        .rx-symbol { font-size: 38px; font-weight: bold; font-family: "Times New Roman", serif; line-height: 1; color: #000; margin-bottom: 10px; }
        .marg-section-title { font-weight: bold; text-transform: uppercase; text-decoration: underline; font-size: 12px; margin-bottom: 4px; }

        /* ✅ Medico Legal Note - Rx box ke bottom right me */
        .marg-medico-legal {
          margin-top: auto;
          text-align: right;
          color: #ff0000;
          font-weight: bold;
          font-style: italic;
          font-size: 11px;
          padding-top: 10px;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        
        .marg-vitals-table { width: 100%; font-size: 12px; }
        .marg-vitals-table td { padding: 3px 0; }
        .marg-vitals-val { border-bottom: 1px dotted #000; padding-left: 3px; font-weight: bold; font-size: 11px; }

        .med-table { width: 100%; border-collapse: collapse; font-size: 10px; } 
        .med-table th { border-bottom: 1px solid #000; padding: 4px; text-align: left; font-weight: bold; }
        .med-table td { padding: 6px 4px; border-bottom: 1px dotted #ccc; vertical-align: top; }
        
        /* ✅ Professional Hospital Footer Styling */
        .marg-footer-text {
          padding: 10px 15px;
          font-size: 11px; /* Thoda bada aur readable font */
          line-height: 1.5; /* Paragraphs aur lines ke beech me acchi spacing */
          text-align: center; /* Center aligned for a professional T&C look */
          white-space: pre-wrap; /* Agar text me paragraph change hai, toh wo sahi se dikhega */
          word-wrap: break-word;
          background: #fff;
          z-index: 1;
          border-top: 1px solid #000; /* Ek separator line jisse footer alag dikhe */
          margin-top: auto; /* Ye property isko humesha page ke bilkul bottom me push kar degi */
        }
        
        @media print {
          @page { size: A4 portrait; margin: 2mm; }  /* uniform ~2mm border all sides (was top:0 → content cut at top) */ 
          html, body { height: auto !important; overflow: visible !important; background: white !important; margin: 0 !important; padding: 0 !important; }
          body * { visibility: hidden; }
          #marg-print-section, #marg-print-section * { visibility: visible; }
          #marg-print-section { position: absolute; left: 0; top: 0; width: 100%; margin: 0 !important; padding: 0 !important; }
          /* Border frame fills the printable area: A4 297mm − 2mm top − 2mm bottom ≈ 293mm (1mm slack to avoid a blank 2nd page) */
          .marg-outer-box { height: 292mm !important; max-height: 292mm !important; margin: 0 !important; }
          .modal, .modal-dialog, .modal-content { border: none !important; box-shadow: none !important; background: transparent !important; padding: 0 !important; }
          .no-print { display: none !important; }
          .marg-title-strip { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .watermark-bg { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .marg-medico-legal { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>

      <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.6)", overflowY: 'auto', zIndex: 9999 }}>
        <div className="modal-dialog modal-lg" style={{ maxWidth: '900px', margin: '0 auto' }}>
          <div className="modal-content border-0">
            <div id="marg-print-section" className="marg-receipt-wrapper p-2 bg-white">
              {pages.map((pageMedicines, pageIndex) => (
                <div className="marg-outer-box" key={`page-${pageIndex}`}>
                  
            {mainLogoPath && <img src={getImageUrl(mainLogoPath)} alt="watermark" className="watermark-bg" />}

                  {/* --- Header --- */}
                  {showHeader ? (
                    <div className="marg-header">
                      {/* ✅ Left Logo - size bada */}
                      <div style={{ width: '140px', height: '100px', display: 'flex', alignItems: 'center' }}>
                     {mainLogoPath && (
                      <img 
                        src={getImageUrl(mainLogoPath)} 
                        alt="Logo" 
                        style={{ width: '100%', maxHeight: '100%', objectFit: 'contain' }} 
                      />
                    )}
                      </div>
                      
                      {/* Center Text */}
                      <div style={{ flexGrow: 1, padding: '0 15px' }}>
                        <h2 style={{ margin: '0 0 4px 0', fontWeight: '900', fontSize: '24px', textTransform: 'uppercase', color: '#ff0000' }}>
                          {companyDetails.company_Name}
                        </h2>
                        <p style={{ margin: '0', fontSize: '13px' }}>{companyDetails.address}</p>
                       <p style={{ margin: '2px 0 0 0', fontSize: '12px' }}>
  <strong>Ph:</strong> {companyDetails.contact1}
  {companyDetails.contact2 && <>, {companyDetails.contact2}</>}
  &nbsp;|&nbsp; <strong>Email:</strong> {companyDetails.email_ID}
</p>
                        
                        {companyDetails.reg_No && companyDetails.reg_No.trim() !== "" && (
                           <p style={{ margin: '2px 0 0 0', fontSize: '12px' }}>
                             <strong>Reg No:</strong> {companyDetails.reg_No}
                           </p>
                        )}
                      </div>

                      {/* ✅ RIGHT SIDE: Ayushman Logo - size bada */}
                      <div style={{ width: '160px', textAlign: 'right', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      {companyDetails?.isAyushmanActive && ayushmanLogoPath && (
                      <img 
                          src={getImageUrl(ayushmanLogoPath)} 
                          alt="Ayushman" 
                          style={{ width: '155px', maxHeight: '100px', objectFit: 'contain' }} 
                      />
                    )}
                      </div>
                    </div>
                  ) : (
                    <div style={{ height: "125px" }}></div>
                  )}

                  {/* --- Title Strip --- */}
                  <div className="marg-title-strip">
                    E-PRESCRIPTION {pages.length > 1 && <span style={{ float: 'right', fontSize: '10px', marginTop: '2px' }}>Page {pageIndex + 1} of {pages.length}</span>}
                  </div>

                  <div className="marg-patient-box">
                    <div className="marg-patient-col marg-patient-left">
                      <table>
                        <tbody>
                          <tr><td width="20%"><strong>Patient</strong></td><td width="5%">:</td><td><strong style={{ fontSize: '14px', textTransform: 'uppercase' }}>{patientName}</strong></td></tr>
                          <tr><td><strong>UHID No.</strong></td><td>:</td><td>{uhid}</td></tr>
                          <tr><td><strong>Address</strong></td><td>:</td><td>{data.address || "-"}</td></tr>
                          <tr><td><strong>Doctor</strong></td><td>:</td><td><strong>{doctorName}</strong></td></tr>
                        </tbody>
                      </table>
                    </div>
                    
                    <div className="marg-patient-col marg-patient-right">
                      <table style={{ width: '100%' }}>
                        <tbody>
                         {/* --- Combined OPD/IPD ID and DSR No --- */}
      <tr>
        <td width="40%" style={{ textAlign: 'right' }}><strong>{visitType} No</strong></td>
        <td width="5%" style={{ textAlign: 'center' }}>:</td>
        <td style={{ textAlign: 'left', whiteSpace: 'nowrap' }}>
          <strong style={{ fontSize: '14px' }}>{receiptNo}</strong>
          
          {/* DSR No Box */}
          {(data.dailySrNo ?? data.DailySrNo) ? (
            <span style={{ 
              marginLeft: '10px', 
              padding: '2px 5px', 
              border: '1px solid #000', 
              borderRadius: '4px', 
              fontSize: '13px',
              backgroundColor: '#f8f9fa'
            }}>
              <strong>DSR: {data.dailySrNo ?? data.DailySrNo}</strong>
            </span>
          ) : null}
        </td>
      </tr>
                          <tr><td style={{ textAlign: 'right' }}><strong>Date</strong></td><td style={{ textAlign: 'center' }}>:</td><td style={{ textAlign: 'left' }}>{formatDateToDDMMYYYY(prescriptionDate)}</td></tr>
                          
                          <tr><td style={{ textAlign: 'right' }}><strong>Age/Sex</strong></td><td style={{ textAlign: 'center' }}>:</td><td style={{ textAlign: 'left' }}>{ageGender}</td></tr>
                          <tr><td style={{ textAlign: 'right' }}><strong>Mobile No.</strong></td><td style={{ textAlign: 'center' }}>:</td><td style={{ textAlign: 'left' }}>{mobile}</td></tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="marg-rx-container">
                    
                    {/* LEFT SIDE (25%) - Only Vitals & Investigations */}
                    <div className="marg-rx-left">
                      {pageIndex === 0 && (
                        <>
                          <div>
                            <div className="marg-section-title">VITALS</div>
                            <table className="marg-vitals-table">
                        <tbody>
                          {vitals.BP && <tr><td width="40%">BP</td><td width="5%">:</td><td className="marg-vitals-val">{vitals.BP} mmHg</td></tr>}
                          {vitals.Pulse && <tr><td>Pulse</td><td>:</td><td className="marg-vitals-val">{vitals.Pulse} b/min</td></tr>}
                          {vitals.SPO2 && <tr><td>SpO2</td><td>:</td><td className="marg-vitals-val">{vitals.SPO2} %</td></tr>}
                          {vitals.Temp && <tr><td>Temp</td><td>:</td><td className="marg-vitals-val">{vitals.Temp}</td></tr>}
                          {vitals.Weight && <tr><td>Weight</td><td>:</td><td className="marg-vitals-val">{vitals.Weight} kg</td></tr>}
                          {vitals.Height && <tr><td>Height</td><td>:</td><td className="marg-vitals-val">{vitals.Height} cm</td></tr>}
                          {vitals.RBS && <tr><td>RBS</td><td>:</td><td className="marg-vitals-val">{vitals.RBS}</td></tr>}
                        </tbody>
                      </table>
                          </div>
                          <div style={{ marginTop: '20px' }}> 
                            <div className="marg-section-title">INVESTIGATIONS</div>
                            {/* ✅ Doctor name yahan se hata diya gaya */}
                          </div>
                        </>
                      )}
                    </div>

                    {/* RIGHT SIDE (75%) */}
                    <div className="marg-rx-right">
                      {pageIndex === 0 && (
                        <>
                          {/* Chief Complaints */}
                          <div>
                            <div className="marg-section-title">CHIEF COMPLAINTS</div>
                            <p style={{ margin: 0, fontSize: '13px', fontStyle: 'italic', whiteSpace: 'pre-wrap' }}>{chiefComplaints || ""}</p>
                          </div>
                          <div style={{ height: '20px' }}></div>
                          
                          {/* PROV DX */}
                          <div>
                            <div className="marg-section-title">PROV DX</div>
                            <p style={{ margin: 0, fontSize: '12px', whiteSpace: 'pre-wrap' }}>{notes || ""}</p>
                          </div>
                          <div style={{ height: '20px' }}></div>

                          {/* Diagnosis */}
                          <div>
                            <div className="marg-section-title">DIAGNOSIS</div>
                            <p style={{ margin: 0, fontSize: '13px', fontWeight: 'bold', whiteSpace: 'pre-wrap' }}>{diagnosis || ""}</p>
                          </div>
                          <div style={{ height: '30px' }}></div>
                        </>
                      )}

                      <div className="rx-symbol">Rx</div>
                      
                      <table className="med-table">
                        <thead>
                          <tr><th width="5%">#</th><th width="45%">Medicine Name</th><th width="15%">Dosage</th><th width="15%">Duration</th><th width="20%">Instructions</th></tr>
                        </thead>
                        <tbody>
                          {pageMedicines.length > 0 ? (
                            pageMedicines.map((med, idx) => (
                              <tr key={idx}>
                                <td>{(pageIndex * ITEMS_PER_PAGE) + idx + 1}.</td>
                                <td><strong>{med.medicineName}</strong></td>
                                <td>{med.dosage || "-"}</td>
                                <td>{med.duration || "-"}</td>
                                <td style={{ fontSize: '9px' }}>{med.instructions || "-"}</td>
                              </tr>
                            ))
                          ) : (
                            <tr><td colSpan="5" style={{ textAlign: 'center', padding: '20px', color: '#999', fontSize: '11px' }}>No medicines prescribed.</td></tr>
                          )}
                        </tbody>
                      </table>

                      {/* ✅ Rx box ke bottom-right me red medico legal note */}
                      <div className="marg-medico-legal">
                        Not For Medico Legal Purpose
                      </div>

                    </div>
                  </div>

                  {/* ✅ DYNAMIC FOOTER RENDER */}
                  {currentFooterText && (
                    <div className="marg-footer-text">
                      {currentFooterText}
                    </div>
                  )}

                </div>
              ))}
            </div>

            <div className="no-print d-flex justify-content-end gap-2 mt-2 pt-2 border-top bg-light p-3 rounded-bottom">
              <button className={`btn ${showHeader ? "btn-outline-secondary" : "btn-warning"} fw-medium px-3`} onClick={handleToggleHeader}>
                <i className={`bi ${showHeader ? "bi-eye-slash" : "bi-eye"} me-2`}></i>{showHeader ? "Use Letterhead (Hide Header)" : "Show Header"}
              </button>
              <button className="btn btn-secondary px-4" onClick={onClose}>Close</button>
              <button className="btn btn-primary px-4 fw-bold" onClick={handlePrint} style={{ background: 'var(--theme-color, #0088cc)', borderColor: 'var(--theme-color, #0088cc)' }}>
                  <i className="bi bi-printer me-2"></i> Print
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PrescriptionPrint;